// usersApi — typed fetchers for /v1/admin/users (Route 7).
import { apiFetch } from "../../lib/api-client.js";

export type UserRoleV2 = "tenant_admin" | "manager" | "officer" | "site_supervisor" | "incident_reporter" | "auditor";
export type UserStatus = "active" | "invited" | "revoked";

export interface MembershipRow {
  id: string;            // membership id (or "line:<li.id>" for LINE-only)
  user_id: string | null;
  email: string;
  display_name: string | null;
  role: UserRoleV2 | string;
  role_legacy: string;
  status: UserStatus;
  created_at: string;
  last_login_at: number | null;
  super_admin: boolean;
  sso_providers: string[];
  webauthn_credentials: number;
  line_user_id?: string | null;
  bound_via?: "email" | "line" | "both";
}

export interface UsersListResponse {
  items: MembershipRow[];
  total: number;
  correlation_id: string | null;
}

export class UsersApiError extends Error {
  status: number; code: string; correlationId: string | null; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyUsersError(err: unknown): string {
  if (err instanceof UsersApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改使用者。";
    if (err.status === 404) return "找不到此使用者紀錄。";
    if (err.status === 400 && err.code === "tenant_id_required")
      return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
    if (err.status === 400 && err.code === "invalid_role") return "指定的角色不在允許清單內。";
    const msgPart = err.upstreamMessage ? `・msg=${err.upstreamMessage}` : "";
    return `使用者資料暫時無法存取（HTTP ${err.status}・code=${err.code}${msgPart}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<UsersApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let msg: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; message?: string };
    code = body.error ?? code; cid = body.correlation_id ?? cid; msg = body.message;
  } catch {}
  return new UsersApiError(r.status, code, cid, msg);
}

export interface UsersQuery {
  tenantId?: string | null; q?: string | null; role?: UserRoleV2 | "" | null; status?: UserStatus | "" | null; limit?: number | null; offset?: number | null;
}

export async function listAdminUsers(params: UsersQuery = {}): Promise<UsersListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.q) qs.set("q", params.q);
  if (params.role) qs.set("role", params.role);
  if (params.status) qs.set("status", params.status);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const r = await apiFetch(`/v1/admin/users${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as UsersListResponse;
}

export async function patchMembership(id: string, body: { role?: UserRoleV2; status?: UserStatus }, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/users/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export async function revokeMembership(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/users/${encodeURIComponent(id)}${qs}`, { method: "DELETE", credentials: "include" });
  if (!r.ok) throw await asError(r);
}

// ── /v1/admin/officers — site assignment management ──────────────────────

export interface OfficerSiteRef {
  site_id: string;
  site_name: string | null;
}

export interface OfficerRow {
  id: string;                           // officer.id (nexus_ops_s00.officers)
  display_name: string;
  status: "active" | "inactive";
  role_v2: string | null;
  line_user_id: string | null;
  created_at: string;
  site_count: number;
  sites: OfficerSiteRef[];
}

export async function listOfficers(params: { tenantId?: string | null; q?: string | null; status?: "active" | "inactive" | null } = {}): Promise<{ items: OfficerRow[] }> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  const r = await apiFetch(`/v1/admin/officers${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: OfficerRow[] };
}

export interface OfficerSiteAssignment {
  site_id: string;
  site_name: string | null;
  site_risk: string | null;
  assigned_at: string;
  status: string;
}

export async function getOfficerSites(officerId: string, tenantId?: string | null): Promise<{ items: OfficerSiteAssignment[] }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/officers/${encodeURIComponent(officerId)}/sites${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: OfficerSiteAssignment[] };
}

export async function putOfficerSites(officerId: string, siteIds: string[], tenantId?: string | null): Promise<{ ok: true; revoked: number; added: number; reactivated: number }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/admin/officers/${encodeURIComponent(officerId)}/sites${qs}`, {
    method: "PUT", headers: { "content-type": "application/json" },
    body: JSON.stringify({ site_ids: siteIds }), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; revoked: number; added: number; reactivated: number };
}

// ── Enroll tokens (face / passkey) ───────────────────────────────────────

export type EnrollKind = "face" | "webauthn";

export interface EnrollTokenResult {
  ok: true;
  kind: EnrollKind;
  token: string;
  web_url: string;
  expires_at: number;
  expires_in_seconds: number;
  reused: boolean;
  already_enrolled: boolean;
}

export async function issueOfficerFaceEnrollToken(officerId: string, tenantId?: string | null): Promise<EnrollTokenResult> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(
    `/v1/admin/officers/${encodeURIComponent(officerId)}/face-enroll-token${qs}`,
    { method: "POST", credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
  return (await r.json()) as EnrollTokenResult;
}

export async function issueOfficerWebauthnEnrollToken(officerId: string, tenantId?: string | null): Promise<EnrollTokenResult> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(
    `/v1/admin/officers/${encodeURIComponent(officerId)}/webauthn-enroll-token${qs}`,
    { method: "POST", credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
  return (await r.json()) as EnrollTokenResult;
}

// ── Enroll status + revoke ───────────────────────────────────────────────

export interface OfficerPasskeyRow {
  id: string;
  device_model: string | null;
  os: string | null;
  enrolled_at: string;
  last_seen_at: string | null;
  status: "active" | "revoked";
}

export interface OfficerEnrollStatus {
  face: {
    enrolled: boolean;
    enrolled_at: number | null;
    pose_count: number;
  };
  passkeys: OfficerPasskeyRow[];
  correlation_id: string | null;
}

export async function getOfficerEnrollStatus(officerId: string, tenantId?: string | null): Promise<OfficerEnrollStatus> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(
    `/v1/admin/officers/${encodeURIComponent(officerId)}/enroll-status${qs}`,
    { credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
  return (await r.json()) as OfficerEnrollStatus;
}

export async function revokeOfficerFace(officerId: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(
    `/v1/admin/officers/${encodeURIComponent(officerId)}/face/revoke${qs}`,
    { method: "POST", credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
}

export async function revokeOfficerPasskey(officerId: string, deviceId: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(
    `/v1/admin/officers/${encodeURIComponent(officerId)}/passkeys/${encodeURIComponent(deviceId)}/revoke${qs}`,
    { method: "POST", credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
}
